import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useNotify,
  SaveButton,
  PasswordInput,
  SelectInput,
  Button as ReactAdminButton
} from 'react-admin';
import { Grid, Typography, FormLabel } from '@material-ui/core/';
import Lock from '@material-ui/icons/Lock';
import withStyles from '@material-ui/core/styles/withStyles';
import UserApi from '../../core/apis/UserApi';
import Confirm from '../../core/components/forked/layout/Confirm';
import DateInput from '../../core/components/forked/input/DateInput';
import moment from 'moment';
import dataProvider from '../../core/providers/IccUserDataProvider';
import messages from '../../core/i18n/englishLanguageMessages';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { isValidPassword } from '../../core/utils';
import EditToolbar from '../../core/components/edit/EditToolbar';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import CustomSimpleForm from '../../core/components/form/CustomSimpleForm';

const styles = {
  headerContainer: {
    paddingTop: '0px !important'
  },
  divStyle: { display: 'flex' },
  labelStyle: {
    cursor: 'pointer',
    color: '#2962FF',
    textDecoration: 'underline',
    marginLeft: '9px'
  },
  label: {
    fontSize: '14px',
    color: '#757575',
    paddingLeft: '2px',
    transform: 'translate(0px, 5px)'
  },
  statusLabel: {
    paddingLeft: '2px',
    paddingTop: '8px'
  },
  resetMfaLinkStyle: {
    display: 'flex',
    alignItems: 'center'
  },
  labelStyleDisabled: {
    color: '#00000061',
    marginLeft: '9px',
    textDecoration: 'underline'
  },
  statusLabelDisabled: {
    paddingLeft: '2px',
    paddingTop: '8px',
    color: '#00000061'
  },
  labelDisabled: {
    fontSize: '14px',
    color: '#00000061',
    paddingLeft: '2px',
    transform: 'translate(0px, 5px)'
  },
  form: {
    '& > div': {
      padding: '0px !important'
    }
  },
  fullWidth: {
    width: '100%'
  }
};

const getDefaultMFA = mfaMethods => {
  const defaultMFA = mfaMethods.find(method =>
    method.mfaMethodDescription.includes('Default')
  );
  return defaultMFA;
};

const isMFAGloballyDisabled = defaultMethod => {
  return defaultMethod.mfaMethodDescription === 'Default/Inherited (Disabled)';
};

const getStatusLabel = enrollmentStatus => {
  return enrollmentStatus === 'ENROLLED'
    ? 'User has successfully enrolled to MFA'
    : 'User has not enrolled to MFA yet';
};

const checkIfValidPassword = value => {
  let message;

  if (value && value.length > 0) {
    if (!isValidPassword(value)) {
      message = 'Please enter a valid password';
    }
  }
  return message;
};

const UserSecurity = props => {
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);
  const [confirmDialogType, setConfirmDialogType] = useState('password');
  const [mfaMethods, setMfaMethods] = useState([]);
  const [mfaMethod, setMfaMethod] = useState('');
  const [mfaDueDate, setMfaDueDate] = useState(null);
  const [mfaStatus, setMfaStatus] = useState('');
  const [loadingState, setLoadingState] = useState(false);
  const [
    showCreateTemporaryPasswordDialogToggle,
    setShowCreateTemporaryPasswordDialogToggle
  ] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [samePasswords, setSamePasswords] = useState(false);
  const [ssoMethods, setSsoMethods] = useState([]);
  const [ssoMethod, setSsoMethod] = useState('');

  const notify = useNotify();
  const notifyServerError = useNotifyServerError();

  const { classes, record, permissions, userProfile = {}, ...rest } = props;

  const [
    hasWriteMfaPermission,
    hasResetPwdPermission,
    hasEditSSOPermission,
    hasReadSSOPermission
  ] = useMemo(
    () => [
      permissions.includes('AUTH_WRITE_MFA'),
      permissions.includes('AUTH_RESET_PASSWORD'),
      permissions.includes('AUTH_EDIT_AUTHENTICATION_MODE'),
      permissions.includes('AUTH_READ_AUTHENTICATION_MODES')
    ],
    [permissions]
  );

  const defaultMFA = getDefaultMFA(mfaMethods);
  const isMupChild = sessionStorage.getItem('isMupChild') === 'true';
  const mfaEnabledAndEditable =
    mfaMethods.length > 0 && !isMFAGloballyDisabled(defaultMFA);
  const ssoEnabled = ssoMethods.length > 0;
  const ssoEditable =
    ssoEnabled &&
    hasEditSSOPermission &&
    (userProfile.userType === 'SUPER_ADMIN' ||
      (userProfile.userType === 'FUND_ADMIN' &&
        userProfile.userId !== record.id));

  const handleConfirmDialogClose = useCallback(() => {
    setConfirmDialogIsOpen(false);
    setLoadingState(false);
  }, []);

  const handleResetClick = useCallback(() => {
    setConfirmDialogIsOpen(true);
    setConfirmDialogType('password');
  }, []);

  const handleConfirm = useCallback(
    async record => {
      try {
        setLoadingState(true);
        await UserApi.resetPassword(record.userId);
        handleConfirmDialogClose();
        notify(messages.ra.notification.password_reset);
      } catch (error) {
        handleConfirmDialogClose();
        notifyServerError(error);
      }
    },
    [notify, notifyServerError, handleConfirmDialogClose]
  );

  const handleResetMfa = useCallback(() => {
    setConfirmDialogIsOpen(true);
    setConfirmDialogType('mfa');
  }, []);

  const handleConfirmResetMfa = useCallback(
    async record => {
      try {
        await UserApi.resetMfa(record.userId);
        handleConfirmDialogClose();
        notify('Multi Factor Authentication was reset succesfully');
      } catch (error) {
        handleConfirmDialogClose();
        notifyServerError(error);
      }
    },
    [notify, notifyServerError, handleConfirmDialogClose]
  );

  const isValidDate = useCallback(value => {
    //null is valid for mfaEnrollmentDueDate
    if (value && !moment(value).isValid()) {
      return 'Invalid Date';
    }
  }, []);

  const handleCreateTemporaryPasswordDialogToggle = useCallback(() => {
    setShowCreateTemporaryPasswordDialogToggle(prevState => !prevState);
  }, []);

  const handleCreateTemporaryPasswordClose = useCallback(() => {
    if (submitting) return;
    handleCreateTemporaryPasswordDialogToggle();
  }, [handleCreateTemporaryPasswordDialogToggle, submitting]);

  const handleCreateTemporaryPasswordClick = useCallback(async () => {
    try {
      await UserApi.setPassword(record.userId, passwordValue);
      notify(messages.ra.notification.set_password_updated);
      handleCreateTemporaryPasswordClose();
    } catch (error) {
      notifyServerError(error);
    }
  }, [
    notify,
    notifyServerError,
    handleCreateTemporaryPasswordClose,
    passwordValue,
    record.userId
  ]);

  const updatePasswordValue = useCallback(event => {
    event.persist();
    setPasswordValue(event.target.value);
  }, []);

  const checkIfSamePassword = useCallback((value, values) => {
    const { confirmPassword, password } = values;
    let message;

    if (!password || !confirmPassword || password !== confirmPassword) {
      setSamePasswords(false);
      message = 'Please enter the same password';
    } else {
      setSamePasswords(true);
    }
    return message;
  }, []);

  const isSoftTokenMethod = useCallback(() => {
    const softTokenIndex = mfaMethods.findIndex(
      method =>
        method.mfaMethodName === mfaMethod &&
        method.mfaMethodDescription.includes('Soft Token OTP')
    );
    return softTokenIndex !== -1;
  }, [mfaMethod, mfaMethods]);

  const handleSubmit = useCallback(
    async (values, mfaEditable, ssoEditable) => {
      if (mfaEditable) {
        setSubmitting(true);
        const formattedDueDate = values.mfaDueDate
          ? moment(values.mfaDueDate)
              .startOf('day')
              .format('YYYY-MM-DDTHH:MM:SS.SSS')
          : values.mfaDueDate;
        const userMfa = {
          userId: record.userId,
          mfaMethod: values.mfaMethod,
          mfaEnrollmentDueDate: formattedDueDate
        };

        try {
          await UserApi.setUserMFA([userMfa]);
          setSubmitting(false);
          setMfaMethod(values.mfaMethod);
          setMfaDueDate(values.mfaDueDate);
          notify('ra.notification.custom_updated_message');
        } catch (error) {
          setSubmitting(false);
          notifyServerError(error);
        }
      }
      if (ssoEditable) {
        setSubmitting(true);
        const userSSO = {
          userId: record.userId,
          authenticationMode: values.ssoMethod
        };

        try {
          await UserApi.setUserSSO(userSSO);
          setSubmitting(false);
          setSsoMethod(values.ssoMethod);
          notify('ra.notification.custom_updated_message');
        } catch (error) {
          setSubmitting(false);
          notifyServerError(error);
        }
      }
    },
    [notify, notifyServerError, record.userId]
  );

  useEffect(() => {
    let isCancelled = false;
    dataProvider('GET_LIST', 'mfamethods', {
      pagination: { perPage: -1 },
      filter: { userId: `${record.userId}` }
    })
      .then(allMFAMethods => {
        if (!isCancelled && allMFAMethods && allMFAMethods.data) {
          const defaultMFA = getDefaultMFA(allMFAMethods.data);
          if (!isMFAGloballyDisabled(defaultMFA)) {
            UserApi.getUserMFA(record.userId)
              .then(userMfaData => {
                if (!isCancelled) {
                  const userMFA =
                    userMfaData && userMfaData.length > 0
                      ? userMfaData[0]
                      : null;
                  setMfaMethods(allMFAMethods.data);
                  setMfaMethod(userMFA ? userMFA.mfaMethod : '');
                  setMfaDueDate(userMFA ? userMFA.mfaEnrollmentDueDate : null);
                  setMfaStatus(
                    userMFA ? userMFA.userMfaEnrollmentStatusDto : ''
                  );
                }
              })
              .catch(notifyServerError);
          } else {
            setMfaMethods(allMFAMethods.data);
            setMfaMethod(defaultMFA.mfaMethodName);
          }
        }
      })
      .catch(notifyServerError);

    if (!isMupChild && hasReadSSOPermission) {
      UserApi.getSSOAuthenticationsModes()
        .then(allSSOModes => {
          if (
            !isCancelled &&
            allSSOModes &&
            allSSOModes.ssoEnabled &&
            allSSOModes.authenticationModes.length > 0
          ) {
            UserApi.getUserSSO(record.userId)
              .then(userSSOMode => {
                if (!isCancelled) {
                  const userMFA = userSSOMode
                    ? userSSOMode.authenticationMode
                    : null;
                  setSsoMethods(allSSOModes.authenticationModes);
                  setSsoMethod(userMFA || '');
                }
              })
              .catch(notifyServerError);
          }
        })
        .catch(notifyServerError);
    }
    return () => (isCancelled = true);
  }, [notify, notifyServerError, hasReadSSOPermission, isMupChild, record]);

  return (
    <div>
      <Grid container spacing={2}>
        {hasResetPwdPermission && (
          <>
            <Grid item xs={12} className={classes.headerContainer}>
              <Typography variant="h5">Security</Typography>
            </Grid>
            <Grid item xs={6} className={classes.divStyle}>
              <Lock color={isMupChild ? 'disabled' : 'inherit'} />
              <div
                onClick={isMupChild ? null : handleResetClick}
                id="reset-password"
              >
                <label
                  className={
                    isMupChild ? classes.labelStyleDisabled : classes.labelStyle
                  }
                >
                  Reset Password
                </label>
              </div>
            </Grid>
            <Grid item xs={6} className={classes.divStyle}>
              <ReactAdminButton
                onClick={handleCreateTemporaryPasswordDialogToggle}
                label="Create Temporary Password"
                variant="outlined"
                color="primary"
                disabled={isMupChild}
                disableFocusRipple
                id="create-temporary"
              />
            </Grid>
            <Dialog open={showCreateTemporaryPasswordDialogToggle}>
              <DialogContent>
                <PasswordInput
                  source="password"
                  label="Password"
                  required
                  variant="standard"
                  validate={checkIfValidPassword}
                  onChange={updatePasswordValue}
                  id="password-1"
                />
                <PasswordInput
                  source="confirmPassword"
                  label="Confirm Password"
                  required
                  variant="standard"
                  validate={checkIfSamePassword}
                  id="password-2"
                />
                <div>Password requirements:</div>
                <div>
                  <ul>
                    <li>8-15 characters long, no spaces allowed</li>
                    <li>include capital letters, numbers and symbols</li>
                    <li>must not be User ID or Email Address</li>
                  </ul>
                </div>
              </DialogContent>
              <DialogActions>
                <SaveButton
                  saving={submitting}
                  handleSubmitWithRedirect={handleCreateTemporaryPasswordClick}
                  label="Create"
                  icon={<></>}
                  id="save"
                  disabled={!samePasswords}
                />
                <Button
                  onClick={handleCreateTemporaryPasswordClose}
                  variant="outlined"
                  color="primary"
                  disabled={submitting}
                  id="cancel"
                >
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        <Grid item xs={6}></Grid>
        <Grid item xs={12}>
          {(mfaEnabledAndEditable || ssoEnabled) && (
            <CustomSimpleForm
              save={values =>
                handleSubmit(values, mfaEnabledAndEditable, ssoEditable)
              }
              toolbar={
                <EditToolbar
                  {...rest}
                  hasEditPermission={hasWriteMfaPermission && !isMupChild}
                  deletable={false}
                />
              }
              variant="standard"
              margin="normal"
              className={classes.form}
              initialValues={{ mfaMethod, mfaDueDate, ssoMethod }}
            >
              {mfaEnabledAndEditable && (
                <Grid container spacing={2} className={classes.fullWidth}>
                  <Grid item xs={12} className={classes.headerContainer}>
                    <Typography variant="h5">
                      Multi Factor Authentication
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={3}>
                    <SelectInput
                      source="mfaMethod"
                      label="MFA Method"
                      choices={mfaMethods}
                      optionText="mfaMethodDescription"
                      optionValue="mfaMethodName"
                      disabled={!hasWriteMfaPermission || isMupChild}
                      variant="standard"
                      id="mfaMethod"
                    />
                  </Grid>
                  {isSoftTokenMethod() && (
                    <React.Fragment>
                      <Grid item className={classes.resetMfaLinkStyle}>
                        <Lock color={isMupChild ? 'disabled' : 'inherit'} />
                        <div
                          onClick={isMupChild ? null : handleResetMfa}
                          id="reset"
                        >
                          <label
                            className={
                              isMupChild
                                ? classes.labelStyleDisabled
                                : classes.labelStyle
                            }
                          >
                            Reset MFA and Send Enrollment Email
                          </label>
                        </div>
                      </Grid>
                      <Grid item>
                        <DateInput
                          label="MFA Enrollment Due Date"
                          source="mfaDueDate"
                          validate={[isValidDate]}
                          placeholder="MM/dd/yyyy"
                          disabled={!hasWriteMfaPermission || isMupChild}
                          id="due-date"
                        />
                      </Grid>
                      <Grid item>
                        <FormLabel
                          className={
                            isMupChild ? classes.labelDisabled : classes.label
                          }
                        >
                          MFA Enrollment Status
                        </FormLabel>
                        <div
                          className={
                            isMupChild
                              ? classes.statusLabelDisabled
                              : classes.statusLabel
                          }
                        >
                          {getStatusLabel(mfaStatus)}
                        </div>
                      </Grid>
                    </React.Fragment>
                  )}
                </Grid>
              )}
              {ssoEnabled && (
                <Grid container spacing={2} className={classes.fullWidth}>
                  <Grid item xs={12} className={classes.headerContainer}>
                    <Typography variant="h5">Single Sign-On</Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={3}>
                    <SelectInput
                      source="ssoMethod"
                      label="Authentication Mode"
                      choices={ssoMethods}
                      optionText="description"
                      optionValue="authenticationMode"
                      disabled={!ssoEditable}
                      variant="standard"
                      id="authentication-mode"
                    />
                  </Grid>
                </Grid>
              )}
            </CustomSimpleForm>
          )}
        </Grid>
      </Grid>
      <Confirm
        isOpen={confirmDialogIsOpen}
        title={
          confirmDialogType === 'password'
            ? 'Reset Password?'
            : 'Reset Multi Factor Authentication?'
        }
        content={
          <div>
            <p style={{ margin: 0 }}>
              {confirmDialogType === 'password'
                ? 'You are about to reset password for this contact.'
                : 'You are about to reset MFA for this contact.'}
            </p>
            {confirmDialogType === 'password'
              ? 'A new temporary password will be sent by email.'
              : 'Email with instructions will be sent.'}
          </div>
        }
        onConfirm={() =>
          confirmDialogType === 'password'
            ? handleConfirm(record)
            : handleConfirmResetMfa(record)
        }
        loading={loadingState}
        onClose={handleConfirmDialogClose}
      />
    </div>
  );
};

export default withStyles(styles)(UserSecurity);
